import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useAuth } from '../../../app/providers';
import { useProfileLogout } from '../../../features/profile/hooks';
import NotificationPanel from '@/features/dashboard/components/NotificationPanel';

interface NavbarProps {
  onMenuClick?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onMenuClick }) => {
  const { user, isAuthenticated } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const { mutate: handleLogout } = useProfileLogout();

  const toggleUserDropdown = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Close dropdowns when clicking outside
  const handleClickOutside = () => {
    if (isUserDropdownOpen) {
      setIsUserDropdownOpen(false);
    }
  };

  return (
    <nav
      className="bg-white shadow-sm border-b border-gray-200"
      onClick={handleClickOutside}
    >
      <div className=" mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex justify-between items-center ">
          {/* Logo Section */}
          <div className="flex items-center gap-2">
            {/* Mobile Sidebar Toggle (Authenticated only) */}
            {isAuthenticated && (
              <button
                onClick={onMenuClick}
                className="lg:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900 focus:outline-none"
                aria-label="Open sidebar"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            )}

            {/* Logo */}
            <Link to="/" className="flex items-center">
              <img
                src="/assets/logos/logo.svg"
                alt="Sober House Hub"
                className="h-8 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation Links - Only show for unauthenticated users */}
          {!isAuthenticated && (
            <div className="hidden md:flex items-center justify-center flex-1 mx-8">
              <div className="flex items-center space-x-8">
                <Link
                  to="/home"
                  className="hover:text-text-primary hover:font-bold  transition-colors duration-200"
                  activeProps={{
                    className: 'text-primary font-bold'
                  }}
                  inactiveProps={{
                    className: 'text-text-secondary'
                  }}
                >
                  Home
                </Link>
                <Link
                  to="/features"
                  className="hover:text-text-primary hover:font-bold transition-colors duration-200"
                  activeProps={{
                    className: 'text-primary font-bold'
                  }}
                  inactiveProps={{
                    className: 'text-text-secondary'
                  }}
                >
                  Features
                </Link>
                <a
                  href="/home#pricing"
                  className="text-text-secondary hover:text-text-primary hover:font-bold font-medium transition-colors duration-200 cursor-pointer"
                >
                  Pricing
                </a>
                <Link
                  to="/about-us"
                  className="hover:text-text-primary hover:font-bold transition-colors duration-200"
                  activeProps={{
                    className: 'text-primary font-bold'
                  }}
                  inactiveProps={{
                    className: 'text-text-secondary'
                  }}
                >
                  About Us
                </Link>
                <Link
                  to="/contact-us"
                  className="hover:text-text-primary hover:font-bold transition-colors duration-200"
                  activeProps={{
                    className: 'text-primary font-bold'
                  }}
                  inactiveProps={{
                    className: 'text-text-secondary'
                  }}
                >
                  Contact Us
                </Link>
              </div>
            </div>
          )}

          {/* Auth Section */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="relative flex items-center gap-4">
                <NotificationPanel />
                <button
                  onClick={toggleUserDropdown}
                  className="flex items-center space-x-3 text-text-secondary cursor-pointer hover:text-text-primary transition-colors duration-200"
                >
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name || 'User'}
                      className="h-12 w-12 rounded-full object-cover border-2 border-gray-200"
                      referrerPolicy="no-referrer"
                      crossOrigin="anonymous"
                    />
                  ) : (
                    <div className="h-12 w-12 rounded-full bg-[#BCD3F5] flex items-center justify-center">
                      <span className="text-lg font-bold text-[#14438A]">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                        {user?.name?.charAt(1)?.toUpperCase() || ''}
                      </span>
                    </div>
                  )}
                </button>

                {/* User Dropdown Menu */}
                {isUserDropdownOpen && (
                  <div className="absolute top-15 right-0 mt-2 w-80  bg-white rounded-3xl shadow-lg p-6 z-50 border border-gray-200">
                    <div className="flex items-center p-2 gap-3 mb-4">
                      <div
                        className={`h-12 w-12 rounded-full bg-[#BCD3F5] flex items-center justify-center ${
                          user?.avatar ? 'hidden' : ''
                        }`}
                      >
                        <span className="text-lg font-bold text-[#14438A]">
                          {user?.name?.charAt(0).toUpperCase()}
                          {user?.name?.charAt(1).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-text-text-primary font-bold text-xl">
                          {user?.name}
                        </p>
                        <p className="text-text-text-secondary">
                          {user?.email}
                        </p>
                      </div>
                    </div>

                    <Link
                      to="/profile"
                      className="flex items-center p-2 gap-4 mb-4"
                      onClick={() => setIsUserDropdownOpen(false)}
                    >
                      {/* user icon  */}
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M4.13765 15.2799C5.00048 14.6576 5.91398 14.1704 6.87815 13.8184C7.84215 13.4666 8.88281 13.2906 10.0001 13.2906C11.1175 13.2906 12.1581 13.4666 13.1221 13.8184C14.0863 14.1704 15.0017 14.6576 15.8684 15.2799C16.5087 14.5781 17.0073 13.7821 17.3641 12.8919C17.7208 12.0017 17.8991 11.0377 17.8991 9.9999C17.8991 7.80523 17.1314 5.94006 15.5959 4.4044C14.0602 2.8689 12.195 2.10115 10.0001 2.10115C7.80531 2.10115 5.94007 2.8689 4.4044 4.4044C2.8689 5.94006 2.10115 7.80523 2.10115 9.9999C2.10115 11.0377 2.27948 12.0017 2.63615 12.8919C2.99298 13.7821 3.49348 14.5781 4.13765 15.2799ZM10.0031 10.7096C9.09865 10.7096 8.33707 10.4014 7.7184 9.7849C7.0999 9.1684 6.79065 8.40781 6.79065 7.50315C6.79065 6.59865 7.0989 5.83706 7.7154 5.2184C8.3319 4.5999 9.09248 4.29065 9.99715 4.29065C10.9016 4.29065 11.6632 4.5989 12.2819 5.2154C12.9004 5.8319 13.2096 6.59248 13.2096 7.49715C13.2096 8.40165 12.9014 9.16323 12.2849 9.7819C11.6684 10.4004 10.9078 10.7096 10.0031 10.7096ZM10.0001 19.2981C8.71265 19.2981 7.50232 19.0546 6.36915 18.5676C5.23615 18.0805 4.25182 17.4183 3.41615 16.5811C2.58048 15.7438 1.91923 14.7592 1.4324 13.6274C0.945565 12.4954 0.702148 11.2855 0.702148 9.99765C0.702148 8.70865 0.945648 7.4984 1.43265 6.3669C1.91982 5.2354 2.58198 4.25182 3.41915 3.41615C4.25648 2.58048 5.24107 1.91923 6.3729 1.4324C7.5049 0.945564 8.71482 0.702148 10.0026 0.702148C11.2916 0.702148 12.5019 0.945648 13.6334 1.43265C14.7649 1.91981 15.7485 2.58198 16.5841 3.41915C17.4198 4.25648 18.0811 5.24015 18.5679 6.37015C19.0547 7.50015 19.2981 8.71015 19.2981 10.0001C19.2981 11.2876 19.0546 12.498 18.5676 13.6311C18.0805 14.7641 17.4183 15.7485 16.5811 16.5841C15.7438 17.4198 14.7601 18.0811 13.6301 18.5679C12.5001 19.0547 11.2901 19.2981 10.0001 19.2981ZM9.9999 17.8991C10.8892 17.8991 11.744 17.7573 12.5641 17.4736C13.3843 17.19 14.1303 16.7872 14.8021 16.2654C14.1303 15.7712 13.394 15.3851 12.5931 15.1069C11.7925 14.8287 10.9287 14.6896 10.0019 14.6896C9.07523 14.6896 8.2064 14.8242 7.3954 15.0934C6.5844 15.3626 5.85198 15.7532 5.19815 16.2654C5.86998 16.7872 6.6159 17.19 7.4359 17.4736C8.2559 17.7573 9.11056 17.8991 9.9999 17.8991ZM10.0001 9.31065C10.506 9.31065 10.9342 9.1354 11.2849 8.7849C11.6354 8.43423 11.8106 8.00598 11.8106 7.50015C11.8106 6.99431 11.6354 6.56606 11.2849 6.2154C10.9342 5.8649 10.506 5.68965 10.0001 5.68965C9.49432 5.68965 9.06606 5.8649 8.7154 6.2154C8.3649 6.56606 8.18965 6.99431 8.18965 7.50015C8.18965 8.00598 8.3649 8.43423 8.7154 8.7849C9.06606 9.1354 9.49432 9.31065 10.0001 9.31065Z"
                          fill="#5761EB"
                        />
                      </svg>

                      <p className="text-text-text-primary">Update Profile</p>
                    </Link>

                    <Link
                      to="/change-password"
                      className="flex items-center p-2 gap-4 mb-4"
                      onClick={() => setIsUserDropdownOpen(false)}
                    >
                      <svg
                        width="22"
                        height="12"
                        viewBox="0 0 22 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1.40362 11.6286V10.2296H20.5884V11.6286H1.40362ZM2.18537 5.68531L1.21537 5.12955L2.13062 3.53355H0.307617V2.41431H2.13062L1.21537 0.868054L2.18537 0.312305L3.09312 1.85855L4.00087 0.312305L4.97112 0.868054L4.05087 2.41431H5.87862V3.53355H4.05087L4.97112 5.12955L4.00087 5.68531L3.09312 4.08931L2.18537 5.68531ZM10.0844 5.68531L9.12212 5.12955L10.0296 3.5258H8.20662V2.41431H10.0296L9.12212 0.855554L10.0844 0.299805L10.9921 1.85855L11.9076 0.299805L12.8701 0.855554L11.9624 2.41431H13.7854V3.5258H11.9624L12.8701 5.12955L11.9076 5.68531L10.9921 4.08931L10.0844 5.68531ZM17.9911 5.68531L17.0211 5.12955L17.9411 3.5258H16.1134V2.41431H17.9411L17.0211 0.855554L17.9911 0.299805L18.8989 1.85855L19.8066 0.299805L20.7769 0.855554L19.8614 2.41431H21.6844V3.5258H19.8614L20.7769 5.12955L19.8066 5.68531L18.8989 4.08931L17.9911 5.68531Z"
                          fill="#5761EB"
                        />
                      </svg>

                      <p className="text-text-text-primary">Update Password</p>
                    </Link>

                    <button
                      className="flex items-center p-2 gap-4 cursor-pointer"
                      onClick={() => {
                        handleLogout();
                        setIsUserDropdownOpen(false);
                      }}
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2.40665 17.3991C1.93415 17.3991 1.5319 17.2331 1.1999 16.9011C0.868065 16.5693 0.702148 16.1664 0.702148 15.6924V2.4089C0.702148 1.9349 0.868065 1.5319 1.1999 1.1999C1.5319 0.868064 1.93415 0.702148 2.40665 0.702148H9.0299V2.10115H2.4089C2.3319 2.10115 2.26132 2.13315 2.19715 2.19715C2.13315 2.26131 2.10115 2.3319 2.10115 2.4089V15.6924C2.10115 15.7694 2.13315 15.8399 2.19715 15.9039C2.26132 15.9681 2.3319 16.0001 2.4089 16.0001H9.0299V17.3991H2.40665ZM13.2106 13.1356L12.2251 12.1444L14.6194 9.75015H6.1569V8.35115H14.6194L12.2251 5.9569L13.2106 4.9609L17.2981 9.04815L13.2106 13.1356Z"
                          fill="#5761EB"
                        />
                      </svg>
                      <p className="text-text-text-primary">Logout</p>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/sign-in"
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#5761EB] hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Login
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button - Only show for unauthenticated users or user dropdown */}
          <div className="md:hidden">
            {isAuthenticated ? (
              <button
                onClick={toggleUserDropdown}
                className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 transition-colors duration-200"
              >
                {user?.avatar ? (
                  <img
                    src={user.avatar}
                    alt={user.name}
                    className="h-8 w-8 rounded-full"
                  />
                ) : (
                  <div className="h-8 w-8 rounded-full bg-indigo-600 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            ) : (
              <button
                onClick={toggleMobileMenu}
                className="text-text-secondary hover:text-text-primary transition-colors duration-200"
                aria-label="Toggle mobile menu"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  {isMobileMenuOpen ? (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  )}
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Mobile Menu - Only show for unauthenticated users */}
        {isMobileMenuOpen && !isAuthenticated && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 border-t border-gray-200">
              {/* Navigation Links */}
              <Link
                to="/home"
                className="block px-3 py-2 hover:text-text-primary hover:font-bold font-medium transition-colors duration-200"
                activeProps={{
                  className: 'text-primary font-bold'
                }}
                inactiveProps={{
                  className: 'text-text-secondary'
                }}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </Link>
              <a
                href="/features"
                className="block px-3 py-2 text-text-secondary hover:text-text-primary hover:font-bold font-medium transition-colors duration-200"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Features
              </a>
              <a
                href="/about-us"
                className="block px-3 py-2 text-text-secondary hover:text-text-primary hover:font-bold font-medium transition-colors duration-200"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                About Us
              </a>
              <a
                href="#pricing"
                className="block px-3 py-2 text-text-secondary hover:text-text-primary hover:font-bold font-medium transition-colors duration-200"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Pricing
              </a>
               <a
                href="/contact-us"
                className="block px-3 py-2 text-text-secondary hover:text-text-primary hover:font-bold font-medium transition-colors duration-200"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Contact Us
              </a>

              {/* Auth Section */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <Link
                  to="/sign-in"
                  className="block px-3 py-2 text-text-secondary hover:text-text-primary font-medium transition-colors duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
                <Link
                  to="/sign-up"
                  className="block mx-3 my-2 btn-primary text-center"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </div>
            </div>
          </div>
        )}

        {/* Authenticated Mobile User Dropdown */}
        {isUserDropdownOpen && isAuthenticated && (
          <div className="md:hidden absolute right-4 top-16 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
            <div className="px-4 py-2 border-b border-gray-200">
              <p className="text-sm font-medium text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
            </div>
            <Link
              to="/profile"
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => setIsUserDropdownOpen(false)}
            >
              Profile
            </Link>
            <Link
              to="/change-password"
              className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              onClick={() => setIsUserDropdownOpen(false)}
            >
              Update Password
            </Link>
            <hr className="my-1" />
            <button
              onClick={() => {
                handleLogout();
                setIsUserDropdownOpen(false);
              }}
              className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
            >
              Logout
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
